// Lazy-load extended translations only when a non-English language is requested.
// This saves ~73KB from the initial bundle.
let _extendedLoaded = false;

export async function loadExtendedTranslations() {
    if (_extendedLoaded) return;
    const { extendedTranslations } = await import('./extended.js');
    Object.keys(extendedTranslations).forEach(lang => {
        if (locales[lang]) {
            Object.assign(locales[lang], extendedTranslations[lang]);
        } else {
            locales[lang] = extendedTranslations[lang];
        }
    });
    _extendedLoaded = true;
}

export const locales = {
    en: {
        // HUD & Navigation
        APP_TITLE: "CYTIFIC KB TESTER",
        APP_VERSION: "Advanced Browser-Based Keyboard Diagnostics",
        FOCUS_LABEL: "Focus",
        FOCUS_ACTIVE: "ACTIVE",
        FOCUS_INACTIVE: "INACTIVE",
        LAST_KEY: "Last",
        CAPS_LOCK: "Caps Lock",
        NUM_LOCK: "Num Lock",
        SCROLL_LOCK: "Scroll Lock",
        SYSTEM_READY: "SYSTEM_READY",
        TYPING_TEST_PANGRAM: "The quick brown fox jumps over the lazy dog",
        FOCUS_MODE_BTN: "FOCUS_MODE - ESC",

        // Metrics
        METRIC_POLLING: "Polling Rate",
        METRIC_LATENCY: "Latency",
        METRIC_ACTIVE: "Active",
        METRIC_MAX_ROLL: "Max Roll",
        METRIC_APM: "APM",
        METRIC_CPS: "CPS",
        METRIC_TOTAL_PRESSES: "Total Pressed",
        UNIT_HZ: "Hz",
        UNIT_MS: "ms",
        UNIT_KEYS: "KEYS",
        UNIT_APM: "APM",
        UNIT_CPS: "CPS",
        UNIT_PRESSES: "PRESSES",

        // Metric Descriptions (tooltips)
        DESC_POLLING: "Polling Rate — How often (in Hz) your keyboard reports to the PC. Higher = faster response. Most keyboards: 125–1000 Hz. Browser-estimated.",
        DESC_LATENCY: "Input Latency — Delay (in ms) between pressing a key and the browser registering it. Lower = better. Affected by USB polling, OS, and browser overhead.",
        DESC_ACTIVE: "Active Keys — Number of keys currently held down simultaneously. Tests your keyboard's real-time multi-key capability.",
        DESC_MAX_ROLL: "Max Rollover (NKRO) — Maximum number of keys registered at once during this session. Full NKRO keyboards support 10+ simultaneous keys.",
        DESC_APM: "Actions Per Minute — Total keystrokes + clicks per minute over a rolling 60-second window. Pro gamers average 200–400 APM.",
        DESC_CPS: "Clicks Per Second — Burst input rate over the last 1 second. Measures your fastest tapping speed. Competitive clickers reach 10–15 CPS.",
        DESC_TOTAL_PRESSES: "Total Key Presses — Cumulative count of all unique keydown events in this session (excludes OS auto-repeat). Resets on page refresh.",

        // Toolbar
        UI_STYLE: "Style",
        UI_LAYOUT: "Layout",
        UI_STANDARD: "Standard Layout",
        UI_SOUND: "Sound",
        UI_LANGUAGE: "Language",

        // Terminal Log
        LOG_TITLE: "Event Stream",
        LOG_RESUMED: "Input Resumed",
        LOG_CLEARED: "Logs Cleared",

        // Modals
        BTN_OK: "OK",
        BTN_CLOSE: "Close",
        BTN_CANCEL: "Cancel",

        // Info Sections
        HDR_HOW_IT_WORKS: "HOW IT WORKS",
        HDR_HOW_IT_WORKS_SUB: "Three simple steps to test any keyboard",
        HDR_DIAGNOSTICS: "KEYBOARD DIAGNOSTICS GUIDE",
        HDR_DIAGNOSTICS_SUB: "Understanding your keyboard's capabilities and limits",
        HDR_PRO_TIPS: "PRO TIPS & TRICKS",
        HDR_PRO_TIPS_SUB: "Get the most out of your keyboard testing session",
        HDR_FAQ: "FREQUENTLY ASKED QUESTIONS",
        HDR_FAQ_SUB: "Everything you need to know about keyboard testing",
        AD_NOTICE: "Ads help us keep this tool free. Thank you for your patience!",
        SUPPORT_US_TITLE: "Why Advertisement?",
        SUPPORT_US_DESC: "Running a high-performance, real-time diagnostic tool requires significant server and development resources. Ads help us cover these costs and keep the project free for everyone. We appreciate your understanding.",
        SCROLL_DOWN: "Scroll for Diagnostics & Tips",
        PANEL_SCROLL_HINT: "Scroll for Tools"
    },

    es: {
        APP_TITLE: "CYTIFIC KB TESTER",
        APP_VERSION: "YOUR GENERIC SECURED KEYBOARD TESTER",
        FOCUS_LABEL: "FOCO",
        FOCUS_ACTIVE: "ACTIVO",
        FOCUS_INACTIVE: "INACTIVO",
        LAST_KEY: "ÚLTIMA",
        CAPS_LOCK: "MAYÚS",
        NUM_LOCK: "BLOQ_NUM",
        SCROLL_LOCK: "BLOQ_DESPL",
        SYSTEM_READY: "SISTEMA_LISTO",
        TYPING_TEST_PANGRAM: "El veloz murciélago hindú comía feliz cardo y trigo",
        FOCUS_MODE_BTN: "MODO_FOCO - ESC",

        METRIC_POLLING: "POLLING_RATE",
        METRIC_LATENCY: "LATENCIA",
        METRIC_ACTIVE: "ACTIVO",
        METRIC_MAX_ROLL: "MAX_ROLL",
        METRIC_APM: "APM",
        METRIC_CPS: "CPS",
        UNIT_HZ: "Hz",
        UNIT_MS: "ms",
        UNIT_KEYS: "TECLAS",
        UNIT_APM: "APM",
        UNIT_CPS: "CPS",
        UNIT_PRESSES: "PULSACIONES",
        METRIC_TOTAL_PRESSES: "TOTAL_PULSADO",

        UI_STYLE: "ESTILO",
        UI_LAYOUT: "DISPOSICIÓN",
        UI_STANDARD: "ESTÁNDAR",
        UI_SOUND: "SONIDO",
        UI_LANGUAGE: "IDIOMA",

        LOG_TITLE: "FLUJO_EVENTOS",
        LOG_RESUMED: "ENTRADA_REANUDADA",
        LOG_CLEARED: "REGISTROS_BORRADOS",

        BTN_OK: "Aceptar",
        BTN_CLOSE: "Cerrar",
        BTN_CANCEL: "Cancelar",

        HDR_HOW_IT_WORKS: "CÓMO FUNCIONA",
        HDR_HOW_IT_WORKS_SUB: "Tres sencillos pasos para probar cualquier teclado",
        HDR_DIAGNOSTICS: "GUÍA DE DIAGNÓSTICO DE TECLADO",
        HDR_DIAGNOSTICS_SUB: "Entendiendo las capacidades de tu teclado",
        HDR_PRO_TIPS: "CONSEJOS Y TRUCOS PRO",
        HDR_PRO_TIPS_SUB: "Aprovecha al máximo tu sesión de pruebas",
        HDR_FAQ: "PREGUNTAS FRECUENTES",
        HDR_FAQ_SUB: "Todo lo que necesitas saber",
        PANEL_SCROLL_HINT: "Desplázate para Herramientas"
    },

    fr: {
        APP_TITLE: "CYTIFIC KB TESTER",
        APP_VERSION: "YOUR GENERIC SECURED KEYBOARD TESTER",
        FOCUS_LABEL: "FOCUS",
        FOCUS_ACTIVE: "ACTIF",
        FOCUS_INACTIVE: "INACTIF",
        LAST_KEY: "DERNIÈRE",
        CAPS_LOCK: "VERR_MAJ",
        NUM_LOCK: "VERR_NUM",
        SCROLL_LOCK: "ARRÊT_DÉFIL",
        SYSTEM_READY: "SYSTÈME_PRÊT",
        TYPING_TEST_PANGRAM: "Portez ce vieux whisky au juge blond qui fume",
        FOCUS_MODE_BTN: "MODE_FOCUS - ESC",

        METRIC_POLLING: "SONDAGE",
        METRIC_LATENCY: "LATENCE",
        METRIC_ACTIVE: "ACTIF",
        METRIC_MAX_ROLL: "ROLL_MAX",
        UNIT_HZ: "HZ",
        UNIT_MS: "MS",
        UNIT_KEYS: "TOUCHES",
        METRIC_TOTAL_PRESSES: "TOTAL_APPUYÉ",
        UNIT_PRESSES: "APPUIS",

        UI_STYLE: "STYLE",
        UI_LAYOUT: "DISPOSITION",
        UI_STANDARD: "STANDARD",
        UI_SOUND: "SON",
        UI_LANGUAGE: "LANGUE",

        LOG_TITLE: "FLUX_ÉVÉNEMENTS",
        LOG_RESUMED: "SAISIE_REPRISE",
        LOG_CLEARED: "JOURNAUX_EFFACÉS",

        BTN_OK: "OK",
        BTN_CLOSE: "Fermer",
        BTN_CANCEL: "Annuler",

        HDR_HOW_IT_WORKS: "COMMENT ÇA MARCHE",
        HDR_HOW_IT_WORKS_SUB: "Trois étapes simples pour tester n'importe quel clavier",
        HDR_DIAGNOSTICS: "GUIDE DE DIAGNOSTIC CLAVIER",
        HDR_DIAGNOSTICS_SUB: "Comprendre les limites de votre clavier",
        HDR_PRO_TIPS: "CONSEILS ET ASTUCES PRO",
        HDR_PRO_TIPS_SUB: "Tirez le meilleur parti de votre test",
        HDR_FAQ: "FOIRE AUX QUESTIONS",
        HDR_FAQ_SUB: "Tout ce que vous devez savoir sur les tests de clavier",
        PANEL_SCROLL_HINT: "Faire défiler pour Outils"
    },

    de: {
        APP_TITLE: "CYTIFIC KB TESTER",
        APP_VERSION: "YOUR GENERIC SECURED KEYBOARD TESTER",
        FOCUS_LABEL: "FOKUS",
        FOCUS_ACTIVE: "AKTIV",
        FOCUS_INACTIVE: "INAKTIV",
        LAST_KEY: "LETZTE",
        CAPS_LOCK: "FESTSTELL",
        NUM_LOCK: "NUM_BLOCK",
        SCROLL_LOCK: "ROLLEN",
        SYSTEM_READY: "SYSTEM_BEREIT",
        TYPING_TEST_PANGRAM: "Franz jagt im komplett verwahrlosten Taxi quer durch Bayern",
        FOCUS_MODE_BTN: "FOKUS_MODUS - ESC",

        METRIC_POLLING: "POLLING_RATE",
        METRIC_LATENCY: "LATENZ",
        METRIC_ACTIVE: "AKTIV",
        METRIC_MAX_ROLL: "MAX_ROLL",
        METRIC_APM: "APM",
        METRIC_CPS: "CPS",
        UNIT_HZ: "Hz",
        UNIT_MS: "ms",
        UNIT_KEYS: "TASTEN",
        UNIT_APM: "APM",
        UNIT_CPS: "CPS",
        UNIT_PRESSES: "DRÜCKE",
        METRIC_TOTAL_PRESSES: "GESAMT_GEDRÜCKT",

        UI_STYLE: "STIL",
        UI_LAYOUT: "LAYOUT",
        UI_STANDARD: "STANDARD",
        UI_SOUND: "KLANG",
        UI_LANGUAGE: "SPRACHE",

        LOG_TITLE: "EREIGNIS_STREAM",
        LOG_RESUMED: "EINGABE_FORTGESETZT",
        LOG_CLEARED: "PROTOKOLLE_GELÖSCHT",

        BTN_OK: "OK",
        BTN_CLOSE: "Schließen",
        BTN_CANCEL: "Abbrechen",

        HDR_HOW_IT_WORKS: "WIE ES FUNKTIONIERT",
        HDR_HOW_IT_WORKS_SUB: "Drei einfache Schritte zum Testen jeder Tastatur",
        HDR_DIAGNOSTICS: "TASTATUR DIAGNOSTIK LEITFADEN",
        HDR_DIAGNOSTICS_SUB: "Die Fähigkeiten Ihrer Tastatur verstehen",
        HDR_PRO_TIPS: "PROFI TIPPS & TRICKS",
        HDR_PRO_TIPS_SUB: "Holen Sie das meiste aus Ihrem Test heraus",
        HDR_FAQ: "HÄUFIG GESTELLTE FRAGEN",
        HDR_FAQ_SUB: "Alles, was Sie wissen müssen",
        PANEL_SCROLL_HINT: "Scrollen für Werkzeuge"
    },

    ja: {
        APP_TITLE: "CYTIFIC KB TESTER",
        APP_VERSION: "YOUR GENERIC SECURED KEYBOARD TESTER",
        FOCUS_LABEL: "フォーカス",
        FOCUS_ACTIVE: "アクティブ",
        FOCUS_INACTIVE: "非アクティブ",
        LAST_KEY: "最後",
        CAPS_LOCK: "CAPS_LOCK",
        NUM_LOCK: "NUM_LOCK",
        SCROLL_LOCK: "SCROLL_LOCK",
        SYSTEM_READY: "システム準備完了",
        TYPING_TEST_PANGRAM: "いろはにほへと ちりぬるを わかよたれそ つねならむ",
        FOCUS_MODE_BTN: "集中モード - ESC",

        METRIC_POLLING: "ポーリング",
        METRIC_LATENCY: "遅延",
        METRIC_ACTIVE: "アクティブ",
        METRIC_MAX_ROLL: "最大同時押し",
        METRIC_APM: "APM",
        METRIC_CPS: "CPS",
        UNIT_HZ: "Hz",
        UNIT_MS: "ms",
        UNIT_KEYS: "キー",
        UNIT_APM: "APM",
        UNIT_CPS: "CPS",
        UNIT_PRESSES: "回",
        METRIC_TOTAL_PRESSES: "合計打鍵",

        UI_STYLE: "スタイル",
        UI_LAYOUT: "レイアウト",
        UI_STANDARD: "標準配列",
        UI_SOUND: "サウンド",
        UI_LANGUAGE: "言語",

        LOG_TITLE: "イベントストリーム",
        LOG_RESUMED: "入力再開",
        LOG_CLEARED: "ログ消去",

        BTN_OK: "確認",
        BTN_CLOSE: "閉じる",
        BTN_CANCEL: "キャンセル",

        HDR_HOW_IT_WORKS: "使い方",
        HDR_HOW_IT_WORKS_SUB: "３つの簡単なステップでテスト",
        HDR_DIAGNOSTICS: "キーボード診断ガイド",
        HDR_DIAGNOSTICS_SUB: "キーボードの性能と限界を理解する",
        HDR_PRO_TIPS: "プロのヒント",
        HDR_PRO_TIPS_SUB: "テストセッションを最大限に活用する",
        HDR_FAQ: "よくある質問",
        HDR_FAQ_SUB: "キーボードテストについて知っておくべきこと",
        PANEL_SCROLL_HINT: "スクロールしてツールを見る"
    },

    zh: {
        APP_TITLE: "CYTIFIC KB TESTER",
        APP_VERSION: "YOUR GENERIC SECURED KEYBOARD TESTER",
        FOCUS_LABEL: "焦点",
        FOCUS_ACTIVE: "活动",
        FOCUS_INACTIVE: "非活动",
        LAST_KEY: "最后按键",
        CAPS_LOCK: "大写锁定",
        NUM_LOCK: "数字锁定",
        SCROLL_LOCK: "滚动锁定",
        SYSTEM_READY: "系统就绪",
        TYPING_TEST_PANGRAM: "程序员经常去咖啡厅写代码，享受安静的环境和美味的拿铁。",
        FOCUS_MODE_BTN: "专注模式 - ESC",

        METRIC_POLLING: "回报率",
        METRIC_LATENCY: "延迟",
        METRIC_ACTIVE: "输入中",
        METRIC_MAX_ROLL: "全键无冲",
        METRIC_APM: "APM",
        METRIC_CPS: "CPS",
        UNIT_HZ: "Hz",
        UNIT_MS: "ms",
        UNIT_KEYS: "键",
        UNIT_APM: "APM",
        UNIT_CPS: "CPS",
        UNIT_PRESSES: "次",
        METRIC_TOTAL_PRESSES: "总按键",

        UI_STYLE: "皮肤",
        UI_LAYOUT: "大小",
        UI_STANDARD: "配列",
        UI_SOUND: "音效",
        UI_LANGUAGE: "语言",

        LOG_TITLE: "事件流",
        LOG_RESUMED: "输入已恢复",
        LOG_CLEARED: "日志已清除",

        BTN_OK: "确定",
        BTN_CLOSE: "关闭",
        BTN_CANCEL: "取消",

        HDR_HOW_IT_WORKS: "如何使用",
        HDR_HOW_IT_WORKS_SUB: "简单的三步即可测试任何键盘",
        HDR_DIAGNOSTICS: "键盘诊断指南",
        HDR_DIAGNOSTICS_SUB: "了解键盘的功能与极限",
        HDR_PRO_TIPS: "高级技巧",
        HDR_PRO_TIPS_SUB: "充分利用你的测试体验",
        HDR_FAQ: "常见问题",
        HDR_FAQ_SUB: "你想了解的关于键盘测试的一切",
        PANEL_SCROLL_HINT: "向下滚动查看工具"
    },

    ko: {
        APP_TITLE: "CYTIFIC KB TESTER",
        APP_VERSION: "YOUR GENERIC SECURED KEYBOARD TESTER",
        FOCUS_LABEL: "포커스",
        FOCUS_ACTIVE: "활성",
        FOCUS_INACTIVE: "비활성",
        LAST_KEY: "마지막",
        CAPS_LOCK: "CAPS",
        NUM_LOCK: "NUM",
        SCROLL_LOCK: "SCROLL",
        SYSTEM_READY: "시스템 준비 완료",
        TYPING_TEST_PANGRAM: "키보드 테스트를 통해 타이핑 속도와 정확도를 측정해보세요.",
        FOCUS_MODE_BTN: "집중 모드 - ESC",

        METRIC_POLLING: "폴링레이트",
        METRIC_LATENCY: "지연시간",
        METRIC_ACTIVE: "활성",
        METRIC_MAX_ROLL: "최대 롤",
        METRIC_APM: "APM",
        METRIC_CPS: "CPS",
        UNIT_HZ: "Hz",
        UNIT_MS: "ms",
        UNIT_KEYS: "키",
        UNIT_APM: "APM",
        UNIT_CPS: "CPS",
        UNIT_PRESSES: "회",
        METRIC_TOTAL_PRESSES: "총_키입력",

        UI_STYLE: "스타일",
        UI_LAYOUT: "크기",
        UI_STANDARD: "표준 배열",
        UI_SOUND: "사운드",
        UI_LANGUAGE: "언어",

        LOG_TITLE: "이벤트 스트림",
        LOG_RESUMED: "입력 재개됨",
        LOG_CLEARED: "로그 지워짐",

        BTN_OK: "확인",
        BTN_CLOSE: "닫기",
        BTN_CANCEL: "취소",

        HDR_HOW_IT_WORKS: "사용 방법",
        HDR_HOW_IT_WORKS_SUB: "모든 키보드를 테스트하는 3가지 간단한 단계",
        HDR_DIAGNOSTICS: "키보드 진단 가이드",
        HDR_DIAGNOSTICS_SUB: "키보드의 성능과 한계 이해하기",
        HDR_PRO_TIPS: "전문가 팁 및 요령",
        HDR_PRO_TIPS_SUB: "테스트 세션을 최대한 활용하기",
        HDR_FAQ: "자주 묻는 질문",
        HDR_FAQ_SUB: "키보드 테스트에 대해 알아야 할 모든 것",
        PANEL_SCROLL_HINT: "도구를 보려면 스크롤하세요"
    },

    pt: {
        APP_TITLE: "CYTIFIC KB TESTER",
        APP_VERSION: "YOUR GENERIC SECURED KEYBOARD TESTER",
        FOCUS_LABEL: "FOCO",
        FOCUS_ACTIVE: "ATIVO",
        FOCUS_INACTIVE: "INATIVO",
        LAST_KEY: "ÚLTIMA",
        CAPS_LOCK: "FIXA",
        NUM_LOCK: "NUM",
        SCROLL_LOCK: "SCROLL",
        SYSTEM_READY: "SISTEMA_PRONTO",
        TYPING_TEST_PANGRAM: "O rápido raposo castanho salta por cima do cão preguiçoso",
        FOCUS_MODE_BTN: "MODO_FOCO - ESC",

        METRIC_POLLING: "TAXA_ATUALIZAÇÃO",
        METRIC_LATENCY: "LATÊNCIA",
        METRIC_ACTIVE: "ATIVAS",
        METRIC_MAX_ROLL: "MÁX_ROLL",
        UNIT_HZ: "HZ",
        UNIT_MS: "MS",
        UNIT_KEYS: "TECLAS",
        METRIC_TOTAL_PRESSES: "TOTAL_PRESSIONADO",
        UNIT_PRESSES: "PRESSIONAMENTOS",

        UI_STYLE: "ESTILO",
        UI_LAYOUT: "LAYOUT",
        UI_STANDARD: "PADRÃO",
        UI_SOUND: "SOM",
        UI_LANGUAGE: "IDIOMA",

        LOG_TITLE: "FLUXO_EVENTOS",
        LOG_RESUMED: "ENTRADA_RETOMADA",
        LOG_CLEARED: "REGISTROS_LIMPOS",

        BTN_OK: "OK",
        BTN_CLOSE: "Fechar",
        BTN_CANCEL: "Cancelar",

        HDR_HOW_IT_WORKS: "COMO FUNCIONA",
        HDR_HOW_IT_WORKS_SUB: "Três passos simples para testar seu teclado",
        HDR_DIAGNOSTICS: "GUIA DE DIAGNÓSTICO",
        HDR_DIAGNOSTICS_SUB: "Entenda os limites do seu teclado",
        HDR_PRO_TIPS: "DICAS PROFISSIONAIS",
        HDR_PRO_TIPS_SUB: "Tire o máximo da sua sessão",
        HDR_FAQ: "PERGUNTAS FREQUENTES",
        HDR_FAQ_SUB: "Tudo o que você precisa saber sobre o teste",
        PANEL_SCROLL_HINT: "Role para Ferramentas"
    },

    ru: {
        APP_TITLE: "CYTIFIC KB TESTER",
        APP_VERSION: "YOUR GENERIC SECURED KEYBOARD TESTER",
        FOCUS_LABEL: "ФОКУС",
        FOCUS_ACTIVE: "АКТИВНО",
        FOCUS_INACTIVE: "НЕАКТИВНО",
        LAST_KEY: "ПОСЛЕДНЯЯ",
        CAPS_LOCK: "CAPS_LOCK",
        NUM_LOCK: "NUM_LOCK",
        SCROLL_LOCK: "SCROLL_LOCK",
        SYSTEM_READY: "СИСТЕМА_ГОТОВА",
        TYPING_TEST_PANGRAM: "Съешь же ещё этих мягких французских булок да выпей чаю",
        FOCUS_MODE_BTN: "РЕЖИМ_ФОКУСА - ESC",

        METRIC_POLLING: "ЧАСТОТА",
        METRIC_LATENCY: "ЗАДЕРЖКА",
        METRIC_ACTIVE: "НАЖАТО",
        METRIC_MAX_ROLL: "МАКС_КЛАВИШ",
        UNIT_HZ: "ГЦ",
        UNIT_MS: "МС",
        UNIT_KEYS: "КЛАВ.",
        METRIC_TOTAL_PRESSES: "ВСЕГО_НАЖАТИЙ",
        UNIT_PRESSES: "НАЖАТИЙ",

        UI_STYLE: "СТИЛЬ",
        UI_LAYOUT: "РАЗМЕР",
        UI_STANDARD: "РАСКЛАДКА",
        UI_SOUND: "ЗВУК",
        UI_LANGUAGE: "ЯЗЫК",

        LOG_TITLE: "ПОТОК_СОБЫТИЙ",
        LOG_RESUMED: "ВВОД_ВОЗОБНОВЛЕН",
        LOG_CLEARED: "ЛОГИ_ОЧИЩЕНЫ",

        BTN_OK: "ОК",
        BTN_CLOSE: "Закрыть",
        BTN_CANCEL: "Отмена",

        HDR_HOW_IT_WORKS: "КАК ЭТО РАБОТАЕТ",
        HDR_HOW_IT_WORKS_SUB: "Три простых шага для проверки вашей клавиатуры",
        HDR_DIAGNOSTICS: "РУКОВОДСТВО ПО ДИАГНОСТИКЕ",
        HDR_DIAGNOSTICS_SUB: "Понимание возможностей и пределов вашей клавиатуры",
        HDR_PRO_TIPS: "ПРОФЕССИОНАЛЬНЫЕ СОВЕТЫ",
        HDR_PRO_TIPS_SUB: "Максимизируйте пользу от тестирования",
        HDR_FAQ: "ЧАСТО ЗАДАВАЕМЫЕ ВОПРОСЫ",
        HDR_FAQ_SUB: "Всё, что вам нужно знать",
        PANEL_SCROLL_HINT: "Прокрутите для инструментов"
    },

    it: {
        APP_TITLE: "CYTIFIC KB TESTER",
        APP_VERSION: "YOUR GENERIC SECURED KEYBOARD TESTER",
        FOCUS_LABEL: "FOCUS",
        FOCUS_ACTIVE: "ATTIVO",
        FOCUS_INACTIVE: "INATTIVO",
        LAST_KEY: "ULTIMA",
        CAPS_LOCK: "BLOC_MAIUSC",
        NUM_LOCK: "BLOC_NUM",
        SCROLL_LOCK: "BLOC_SCORR",
        SYSTEM_READY: "SISTEMA_PRONTO",
        TYPING_TEST_PANGRAM: "Quel vago giovine produce fiori ma non foglie",
        FOCUS_MODE_BTN: "MODO_FOCUS - ESC",

        METRIC_POLLING: "POLLING",
        METRIC_LATENCY: "LATENZA",
        METRIC_ACTIVE: "ATTIVO",
        METRIC_MAX_ROLL: "MAX_ROLL",
        METRIC_APM: "APM",
        METRIC_CPS: "CPS",
        UNIT_HZ: "Hz",
        UNIT_MS: "ms",
        UNIT_KEYS: "TASTI",
        UNIT_APM: "APM",
        UNIT_CPS: "CPS",
        UNIT_PRESSES: "PRESSIONI",
        METRIC_TOTAL_PRESSES: "TOTALE_PREMUTO",

        UI_STYLE: "STILE",
        UI_LAYOUT: "LAYOUT",
        UI_STANDARD: "STANDARD",
        UI_SOUND: "SUONO",
        UI_LANGUAGE: "LINGUA",

        LOG_TITLE: "STREAM_EVENTI",
        LOG_RESUMED: "INPUT_RIPRESO",
        LOG_CLEARED: "REGISTRI_CANCELLATI",

        BTN_OK: "OK",
        BTN_CLOSE: "Chiudi",
        BTN_CANCEL: "Annulla",

        HDR_HOW_IT_WORKS: "COME FUNZIONA",
        HDR_HOW_IT_WORKS_SUB: "Tre semplici passaggi per testare la tastiera",
        HDR_DIAGNOSTICS: "GUIDA DIAGNOSTICA",
        HDR_DIAGNOSTICS_SUB: "Comprendere le capacità della tastiera",
        HDR_PRO_TIPS: "SUGGERIMENTI PRO",
        HDR_PRO_TIPS_SUB: "Sfrutta al meglio la tua sessione",
        HDR_FAQ: "DOMANDE FREQUENTI",
        HDR_FAQ_SUB: "Tutto ciò che devi sapere",
        PANEL_SCROLL_HINT: "Scorri per gli Strumenti"
    },

    tr: {
        APP_TITLE: "CYTIFIC KB TESTER",
        APP_VERSION: "YOUR GENERIC SECURED KEYBOARD TESTER",
        FOCUS_LABEL: "ODAK",
        FOCUS_ACTIVE: "AKTİF",
        FOCUS_INACTIVE: "PASİF",
        LAST_KEY: "SON_TUŞ",
        CAPS_LOCK: "BÜYÜK_HARF",
        NUM_LOCK: "NUM_KİLİDİ",
        SCROLL_LOCK: "KAYDIRMA",
        SYSTEM_READY: "SİSTEM_HAZIR",
        TYPING_TEST_PANGRAM: "Pijamalı hasta, yağız şoföre çabucak güvendi",
        FOCUS_MODE_BTN: "ODAK_MODU - ESC",

        METRIC_POLLING: "TARAMA",
        METRIC_LATENCY: "GECİKME",
        METRIC_ACTIVE: "AKTİF",
        METRIC_MAX_ROLL: "MAKS_TUŞ",
        UNIT_HZ: "HZ",
        UNIT_MS: "MS",
        UNIT_KEYS: "TUŞ",
        METRIC_TOTAL_PRESSES: "TOPLAM_BASILDI",
        UNIT_PRESSES: "BASIM",

        UI_STYLE: "STİL",
        UI_LAYOUT: "BOYUT",
        UI_STANDARD: "DİZİLİM",
        UI_SOUND: "SES",
        UI_LANGUAGE: "DİL",

        LOG_TITLE: "OLAY_AKIS",
        LOG_RESUMED: "GİRİŞ_DEVAM",
        LOG_CLEARED: "GÜNLÜKLER_SİLİNDİ",

        BTN_OK: "Tamam",
        BTN_CLOSE: "Kapat",
        BTN_CANCEL: "İptal",

        HDR_HOW_IT_WORKS: "NASIL ÇALIŞIR",
        HDR_HOW_IT_WORKS_SUB: "Klavyenizi test etmek için 3 basit adım",
        HDR_DIAGNOSTICS: "KLAVYE TEŞHİS REHBERİ",
        HDR_DIAGNOSTICS_SUB: "Klavyenizin yeteneklerini anlayın",
        HDR_PRO_TIPS: "PRO İPUÇLARI",
        HDR_PRO_TIPS_SUB: "Testinizden en iyi şekilde yararlanın",
        HDR_FAQ: "SIKÇA SORULAN SORULAR",
        HDR_FAQ_SUB: "Bilmeniz gereken her şey",
        PANEL_SCROLL_HINT: "Araçlar için Kaydırın"
    },

    hi: {
        APP_TITLE: "CYTIFIC KB TESTER",
        APP_VERSION: "YOUR GENERIC SECURED KEYBOARD TESTER",
        FOCUS_LABEL: "फ़ोकस",
        FOCUS_ACTIVE: "सक्रिय",
        FOCUS_INACTIVE: "निष्क्रिय",
        LAST_KEY: "अंतिम",
        CAPS_LOCK: "CAPS_LOCK",
        NUM_LOCK: "NUM_LOCK",
        SCROLL_LOCK: "SCROLL_LOCK",
        SYSTEM_READY: "सिस्टम_तैयार",
        TYPING_TEST_PANGRAM: "कीबोर्ड टेस्ट से टाइपिंग की गति और सटीकता मापें।",
        FOCUS_MODE_BTN: "फ़ोकस_मोड - ESC",

        METRIC_POLLING: "पोलिंग_रेट",
        METRIC_LATENCY: "विलंबता",
        METRIC_ACTIVE: "सक्रिय",
        METRIC_MAX_ROLL: "अधिकतम_रोल",
        METRIC_APM: "APM",
        METRIC_CPS: "CPS",
        UNIT_HZ: "Hz",
        UNIT_MS: "ms",
        UNIT_KEYS: "KEY",
        UNIT_APM: "APM",
        UNIT_CPS: "CPS",
        UNIT_PRESSES: "दबाव",
        METRIC_TOTAL_PRESSES: "कुल_दबाव",

        DESC_POLLING: "पोलिंग रेट — आपका कीबोर्ड PC को कितनी बार (Hz में) रिपोर्ट करता है। अधिक = तेज़ प्रतिक्रिया।",
        DESC_LATENCY: "इनपुट विलंबता — कुंजी दबाने और ब्राउज़र पंजीकरण के बीच विलंब (ms में)। कम = बेहतर।",
        DESC_ACTIVE: "सक्रिय कुंजियाँ — वर्तमान में एक साथ दबी हुई कुंजियों की संख्या।",
        DESC_MAX_ROLL: "मैक्स रोलओवर (NKRO) — इस सत्र में एक बार में पंजीकृत अधिकतम कुंजियाँ।",
        DESC_APM: "एक्शन प्रति मिनट — 60 सेकंड की विंडो पर कुल कीस्ट्रोक + क्लिक प्रति मिनट।",
        DESC_CPS: "क्लिक प्रति सेकंड — अंतिम 1 सेकंड में बर्स्ट इनपुट दर।",
        DESC_TOTAL_PRESSES: "कुल कुंजी दबाव — इस सत्र में सभी अद्वितीय keydown इवेंट की संचयी गिनती।",

        UI_STYLE: "शैली",
        UI_LAYOUT: "लेआउट",
        UI_STANDARD: "मानक",
        UI_SOUND: "ध्वनि",
        UI_LANGUAGE: "भाषा",

        LOG_TITLE: "इवेंट_स्ट्रीम",
        LOG_RESUMED: "इनपुट_पुनः_शुरू",
        LOG_CLEARED: "लॉग_साफ़",

        BTN_OK: "ठीक है",
        BTN_CLOSE: "बंद करें",
        BTN_CANCEL: "रद्द करें",

        HDR_HOW_IT_WORKS: "यह कैसे काम करता है",
        HDR_HOW_IT_WORKS_SUB: "किसी भी कीबोर्ड का परीक्षण करने के तीन सरल चरण",
        HDR_DIAGNOSTICS: "कीबोर्ड डायग्नोस्टिक गाइड",
        HDR_DIAGNOSTICS_SUB: "अपने कीबोर्ड की क्षमताओं और सीमाओं को समझें",
        HDR_PRO_TIPS: "प्रो टिप्स और ट्रिक्स",
        HDR_PRO_TIPS_SUB: "अपने टेस्टिंग सत्र का अधिकतम लाभ उठाएं",
        HDR_FAQ: "अक्सर पूछे जाने वाले प्रश्न",
        HDR_FAQ_SUB: "कीबोर्ड टेस्टिंग के बारे में सब कुछ",
        AD_NOTICE: "विज्ञापन इस टूल को मुफ़्त रखने में मदद करते हैं। आपके धैर्य के लिए धन्यवाद!",
        SUPPORT_US_TITLE: "विज्ञापन क्यों?",
        SUPPORT_US_DESC: "एक उच्च-प्रदर्शन, रीयल-टाइम डायग्नोस्टिक टूल को चलाने के लिए महत्वपूर्ण सर्वर और विकास संसाधनों की आवश्यकता होती है। विज्ञापन इन लागतों को कवर करने में मदद करते हैं।",
        SCROLL_DOWN: "डायग्नोस्टिक्स और टिप्स के लिए स्क्रॉल करें",
        PANEL_SCROLL_HINT: "टूल्स के लिए स्क्रॉल करें"
    }
};

